/**
 * G17 — skip accountMe invalidate/refetch when there is no customer session.
 * Durable guest silence retired — latch is not product SoT.
 */

import type { Query, QueryClient, QueryFilters } from '@tanstack/react-query';

/** `['customer', tenant, 'account', customerId, 'me' | 'me-validation', ...]`. */
export function isAccountMeQueryKey(queryKey: readonly unknown[]): boolean {
  if (queryKey.length < 5) {
    return false;
  }
  if (queryKey[0] !== 'customer' || queryKey[2] !== 'account') {
    return false;
  }
  const leaf = queryKey[4];
  return leaf === 'me' || leaf === 'me-validation';
}

/**
 * True when accountMe must not be invalidated/refetched (no customer session).
 */
export function shouldSkipAccountMeInvalidation(
  hasCustomerSession: boolean = true,
): boolean {
  return !hasCustomerSession;
}

/**
 * Predicate for invalidateQueries / refetchQueries — drops accountMe when skip applies.
 * When skip does not apply, returns true for every query (caller’s other filters still apply).
 */
export function allowAccountQueryRefresh(
  query: Query,
  hasCustomerSession: boolean = true,
): boolean {
  if (!shouldSkipAccountMeInvalidation(hasCustomerSession)) {
    return true;
  }
  return !isAccountMeQueryKey(query.queryKey);
}

/** Invalidate with accountMe excluded when no session. */
export async function invalidateQueriesSkippingAccountMeWhenGuarded(
  queryClient: QueryClient,
  filters: QueryFilters,
  hasCustomerSession: boolean = true,
): Promise<void> {
  const callerPredicate = filters.predicate;
  await queryClient.invalidateQueries({
    ...filters,
    predicate: (query) => {
      if (!allowAccountQueryRefresh(query, hasCustomerSession)) {
        return false;
      }
      return callerPredicate === undefined ? true : callerPredicate(query);
    },
  });
}

/** Refetch with accountMe excluded when no session. */
export async function refetchQueriesSkippingAccountMeWhenGuarded(
  queryClient: QueryClient,
  filters: QueryFilters,
  hasCustomerSession: boolean = true,
): Promise<void> {
  const callerPredicate = filters.predicate;
  await queryClient.refetchQueries({
    ...filters,
    predicate: (query) => {
      if (!allowAccountQueryRefresh(query, hasCustomerSession)) {
        return false;
      }
      return callerPredicate === undefined ? true : callerPredicate(query);
    },
  });
}
